import { inject, Injectable } from '@angular/core';
import { ReportsUtilsService } from '@app/modules/reports/services/reports-utils.service';
import { ReportsService } from '@app/modules/reports/services/reports.service';
import {
  ApproveReportAction,
  ArchiveReportAction,
  DisapproveReportAction,
  GetReportAction,
  GetReportsListAction,
  ProvisionCustomReportAction,
  ProvisionUploadReportsAction,
  RefreshReportsAction,
  RemoveReportAction,
  SetReportsAction,
  UpdateReportAction,
} from '@app/modules/reports/store/reports.actions';
import {
  Report,
  ReportRisk,
  ReportStatus,
  ReportType,
} from '@app/modules/reports/types/report.types';
import {
  AnalysisType,
  ApiFilters,
  Pagination,
  SortDirection,
  StatePageSettings,
} from '@core/core.types';
import { GetProductInventoryItemAction } from '@app/modules/service-data/store/product-intentory-item.actions';
import { Util } from '@core/utils/core.util';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  ProgressData,
  ProgressDialogComponent,
} from '@shared/components/progress/progress-dialog.component';
import { isEqual } from 'lodash';
import {
  BehaviorSubject,
  EMPTY,
  forkJoin,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { catchError, delay, tap } from 'rxjs/operators';

const RESULTS_STATE_NAME = 'reports';

export interface ReportsStateModel {
  reports: { [id: string]: Report };
  pages?: {
    [key: string]: {
      items: string[];
      continuationToken: string;
    };
  };
  currentPageSettings: StatePageSettings<ReportsFilter>;
  totalCount?: number;
  uploadResult?: string[];
}

@State<ReportsStateModel>({
  name: RESULTS_STATE_NAME,
  defaults: {
    reports: {},
    pages: {},
    uploadResult: [],
    currentPageSettings: {
      pageIndex: 0,
      sortSettings: {
        orderBy: 'Generated',
        sortDirection: SortDirection.desc,
        pageSize: 30,
      },
    },
  },
})
@Injectable()
export class ReportsState {
  private reportsService = inject(ReportsService);
  private reportsUtilsService = inject(ReportsUtilsService);
  private modal = inject(NgbModal);
  private translateService = inject(TranslateService);

  @Selector()
  static getReport(state: ReportsStateModel) {
    return (reportId: string) => state.reports[reportId];
  }

  @Selector()
  static getReportsByRootId(state: ReportsStateModel) {
    return (rootId: string) =>
      Object.values(state.reports).filter((report) => report.rootId === rootId);
  }

  @Selector()
  static getReportsByRootIds(state: ReportsStateModel) {
    return (rootIds: string[]) =>
      rootIds
        .map((rootId) =>
          Object.values(state.reports).filter(
            (report) => report.rootId === rootId
          )
        )
        .flat(1);
  }

  @Selector()
  static getUploadResult(state: ReportsStateModel) {
    return state.uploadResult;
  }

  @Selector()
  static getReportsByIds(state: ReportsStateModel) {
    return (reportIds: string[]) =>
      reportIds
        ?.map((reportId) => state.reports[reportId])
        .filter((report) => !!report) ?? [];
  }

  @Selector()
  static getCurrentPageIds(state: ReportsStateModel) {
    const {
      currentPageSettings: { pageIndex },
      pages,
    } = state;

    if (
      Object.keys(pages).length === 0 ||
      pageIndex > Object.keys(pages).length ||
      pageIndex < 0
    ) {
      return [];
    }
    return state.pages[pageIndex].items.slice();
  }

  @Selector([ReportsState.getCurrentPageIds, ReportsState.getReportsByIds])
  static getCurrentPageReports(
    currentPageIds: string[],
    reportsByIds: (reportIds: string[]) => Report[]
  ) {
    return reportsByIds(currentPageIds);
  }

  @Selector()
  static getPagination({
    currentPageSettings,
    totalCount,
    pages,
  }: ReportsStateModel): Pagination<ReportsFilter> {
    return {
      currentPageSettings: {
        ...currentPageSettings,
      },
      totalCount,
      pagesSize: Object.keys(pages).length,
      collectionSize: currentPageSettings.sortSettings.pageSize,
    };
  }

  @Action(UpdateReportAction)
  updateReportAction(
    { dispatch }: StateContext<ReportsStateModel>,
    { report }: UpdateReportAction
  ) {
    return this.reportsService.updateReport(report).pipe(
      delay(500),
      tap((data) => {
        dispatch(new GetReportAction(data.id, true));
      })
    );
  }

  @Action(ArchiveReportAction)
  archiveReportAction(
    { dispatch }: StateContext<ReportsStateModel>,
    { reportId }: ArchiveReportAction
  ) {
    return this.reportsService.archiveReport(reportId).pipe(
      delay(500),
      switchMap(() => dispatch(new GetReportAction(reportId)))
    );
  }

  @Action(RemoveReportAction)
  removeReportAction(
    { getState, patchState }: StateContext<ReportsStateModel>,
    { reportId }: RemoveReportAction
  ) {
    return this.reportsService.removeReport(reportId).pipe(
      delay(300),
      tap(() => {
        const reportsList = { ...getState().reports };
        delete reportsList[reportId];
        patchState({ reports: reportsList });
      })
    );
  }

  @Action(RefreshReportsAction)
  refreshReportsAction(
    { getState, patchState, dispatch }: StateContext<ReportsStateModel>,
    { reportIds, productInventoryItemIds }: RefreshReportsAction
  ) {
    return forkJoin(
      Util.chunkArray(reportIds, 100).map((ids) =>
        this.reportsService.listReports(
          this.reportsUtilsService.buildHttpParams({ ids }),
          {}
        )
      )
    ).pipe(
      switchMap((responses) => {
        const reports = { ...getState().reports };
        const piiIds = new Set(productInventoryItemIds);

        for (const reportId of reportIds) {
          if (reports[reportId]) {
            piiIds.add(reports[reportId].productInventoryItemId);
          }
          // Not in the response = deleted together with a related report
          delete reports[reportId];
        }
        for (const report of responses.flatMap(({ items }) => items)) {
          reports[report.id] = report;
          piiIds.add(report.productInventoryItemId);
        }
        patchState({ reports });

        return piiIds.size
          ? forkJoin(
              [...piiIds].map((id) =>
                dispatch(new GetProductInventoryItemAction(id, true, true))
              )
            )
          : of(null);
      })
    );
  }

  @Action(GetReportAction)
  getReportAction(
    { getState, patchState }: StateContext<ReportsStateModel>,
    { reportId, prependOnPage = false }: GetReportAction
  ) {
    return this.reportsService.getReport(reportId).pipe(
      tap((report) => {
        const state = getState();
        const updates: Partial<ReportsStateModel> = {
          reports: {
            ...state.reports,
            [report.id]: report,
          },
        };

        if (prependOnPage) {
          const pages = { ...state.pages };
          if (pages[0]) {
            pages[0] = {
              ...pages[0],
              items: [
                report.id,
                ...pages[0].items.filter((id) => id !== report.id),
              ],
            };
            updates.pages = pages;
          }
        }

        patchState(updates);
      })
    );
  }

  @Action(ApproveReportAction)
  approveReportAction(
    { dispatch }: StateContext<ReportsStateModel>,
    { reportId, payload }: ApproveReportAction
  ) {
    return this.reportsService.approveReport(reportId, payload).pipe(
      delay(300),
      switchMap(() => dispatch(new GetReportAction(reportId)))
    );
  }

  @Action(DisapproveReportAction)
  disapproveReportAction(
    { dispatch }: StateContext<ReportsStateModel>,
    { reportId }: DisapproveReportAction
  ) {
    return this.reportsService.disapproveReport(reportId).pipe(
      delay(300),
      switchMap(() => dispatch(new GetReportAction(reportId)))
    );
  }

  @Action(ProvisionCustomReportAction)
  provisionCustomReportAction(
    { dispatch }: StateContext<ReportsStateModel>,
    { report }: ProvisionCustomReportAction
  ) {
    return this.reportsService.provisionCustomReport(report).pipe(
      delay(500),
      switchMap(({ id }) => dispatch(new GetReportAction(id, true)))
    );
  }

  @Action(ProvisionUploadReportsAction)
  provisionUploadReportsAction(
    { patchState }: StateContext<ReportsStateModel>,
    { files, signeeId }: ProvisionUploadReportsAction
  ) {
    const dialogRef = this.modal.open(ProgressDialogComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      modalDialogClass: 'modal-size-md',
    });

    const progressSubject = new BehaviorSubject<ProgressData>({
      status: this.translateService.instant(
        'reports.progress.uploadingReports'
      ),
      value: 1,
      success: false,
    });

    dialogRef.componentInstance.title = 'reports.progress.uploadingReports';
    dialogRef.componentInstance.progressSubject = progressSubject;

    progressSubject.subscribe((resp) => {
      if (resp.success) {
        dialogRef.close();
      }
    });

    return this.uploadRecursively(files, 0, signeeId, [], progressSubject).pipe(
      tap((uploadResult) => patchState({ uploadResult }))
    );
  }

  @Action(GetReportsListAction)
  getReportsListAction(
    { getState, patchState, dispatch }: StateContext<ReportsStateModel>,
    { filters, headers, pageToLoad = 1, reload = false }: GetReportsListAction
  ) {
    // Appends objects are added, in case of separate state @Action calls, as they would otherwise override each other!
    const reportsAppends = {};
    const reportsList = { ...getState().reports };

    const state = getState();
    let pages = { ...state.pages };
    const currentPageSettings = {
      ...state.currentPageSettings,
      sortSettings: { ...state.currentPageSettings.sortSettings },
      filterSettings: { ...state.currentPageSettings.filterSettings },
    };
    let totalCount = state.totalCount;

    let actualPageToLoad = pageToLoad ? pageToLoad - 1 : undefined;

    // Update pagination in case of listing pages.
    if (actualPageToLoad !== undefined) {
      currentPageSettings.pageIndex = actualPageToLoad;

      // Check that filters are same as they were for previous page.
      if (
        !isEqual(currentPageSettings.filterSettings, filters) ||
        currentPageSettings.pageSize !== headers.pagingTop ||
        currentPageSettings.sortSettings.sortDirection !==
          headers.orderDirection ||
        currentPageSettings.sortSettings.orderBy !== headers.orderBy
      ) {
        // If filters are different as they were -> update filters, reset pages object and go back to page 1.
        currentPageSettings.pageSize = headers.pagingTop;
        currentPageSettings.sortSettings.sortDirection = headers.orderDirection;
        currentPageSettings.sortSettings.orderBy = headers.orderBy;
        currentPageSettings.sortSettings.pageSize = headers.pagingTop;
        currentPageSettings.filterSettings = filters;

        actualPageToLoad = 0;
        reload = true;
        pages = {};
        currentPageSettings.pageIndex = actualPageToLoad;
      }
    }

    // If reload is not required, check if we already hold service data with selected properties.
    if (filters.ids && !reload) {
      filters.ids = filters.ids.filter((item) => !reportsList[item]);

      // If we filter out all IDs, return early!
      if (!filters.ids.length) {
        return EMPTY;
      }
    }

    if (filters.ids?.length > 100) {
      return forkJoin(
        Util.chunkArray(filters.ids, 100).map((ids) =>
          dispatch(new GetReportsListAction({ ids }, headers, null, reload))
        )
      );
    }

    // Get selected page continuation token, if not first page
    if (actualPageToLoad > 0) {
      headers.continuationToken =
        pages[actualPageToLoad - 1]?.continuationToken;
    }

    //If not listing page, page doesnt exist or reload is demanded -> call API
    if (actualPageToLoad === undefined || !pages[actualPageToLoad] || reload) {
      return this.reportsService
        .listReports(this.reportsUtilsService.buildHttpParams(filters), headers)
        .pipe(
          tap((response) => {
            response.items.forEach((report) => {
              reportsAppends[report.id] = report;
            });

            // Add/update pages
            if (actualPageToLoad !== undefined) {
              totalCount = response.totalCount;
              pages[actualPageToLoad] = {
                items: response.items.map((report) => report.id),
                continuationToken: response.continuationToken,
              };
            }
          }),
          tap(() => {
            // get latest state again as state might be patched in some other @Action
            const oldState = getState();
            patchState({
              reports: { ...oldState.reports, ...reportsAppends },
              pages,
              totalCount,
              currentPageSettings,
            });
          }),
          catchError(() => {
            patchState({});
            return of(null);
          })
        );
    } else {
      // If page exists -> update state so pagination is updated.
      patchState({ currentPageSettings });
      return EMPTY;
    }
  }

  @Action(SetReportsAction)
  setReportsAction(
    { getState, patchState }: StateContext<ReportsStateModel>,
    { reports }: SetReportsAction
  ) {
    return patchState({
      ...getState(),
      reports: {
        ...getState().reports,
        ...reports.reduce<{ [id: string]: Report }>((acc, report) => {
          acc[report.id] = report;
          return acc;
        }, {}),
      },
    });
  }

  private uploadRecursively(
    files: File[],
    index: number,
    signeeId: string,
    result: string[],
    progressSubject?: BehaviorSubject<ProgressData>
  ): Observable<string[]> {
    progressSubject?.next({
      status: this.translateService.instant(
        'reports.progress.uploadingReportCount',
        { current: index + 1, total: files.length }
      ),
      value: index / files.length,
    });
    const file = files[index];
    return Util.getBase64ImageFromFile(file).pipe(
      switchMap((base64) =>
        this.reportsService.provisionUploadReport({
          id: Util.CreateGuid(),
          data: base64,
          documentName: Util.addAnalysisTypeToName(
            Util.transformBGIFilename(file.name),
            AnalysisType.NIPT
          ),
          signeeId,
        })
      ),
      catchError((err) => {
        result.push(`${file.name} - ${err.error.detail}`);
        return of(err);
      }),
      switchMap(() => {
        index++;
        if (files[index]) {
          return this.uploadRecursively(
            files,
            index,
            signeeId,
            result,
            progressSubject
          );
        } else {
          progressSubject.next({
            value: 1,
            status: this.translateService.instant(
              'reports.progress.reportsUploaded',
              { count: index }
            ),
            success: true,
          });
          return of(result);
        }
      })
    );
  }
}

export interface ReportsFilter extends ApiFilters {
  ids?: string[];
  kitIdToken?: string;
  rootIds?: string[];
  kitIds?: string[];
  analysisType?: AnalysisType;
  importVersion?: string;
  reportType?: ReportType;
  reportReasonToken?: string;
  signeeId?: string;
  detectedRisk?: ReportRisk;
  lastStatuses?: ReportStatus[];
  lastStatus?: ReportStatus;
  lastStatusChangedFrom?: string;
  lastStatusChangedTo?: string;
  status?: ReportStatus;
  laboratories?: string[];
  productIds?: string[];
  partnerIds?: string[];
  countries?: string[];
  reportedRisk?: string;
  statusChangedFrom?: string;
  statusChangedTo?: string;
  generatedFrom?: string;
  generatedTo?: string;
  invalidatedFrom?: string;
  invalidatedTo?: string;
  approvedFrom?: string;
  approvedTo?: string;
  archivedFrom?: string;
  archivedTo?: string;
}
